import { ArrowClockwiseIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { CopyIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import colors from 'tailwindcss/colors';

import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Autocomplete, DatePicker, useApiMutation } from '@/modules/common';
import { MultiSelect } from '@/modules/common/components/multi-select';
import { EventType } from '@/modules/eventsV2/types';

import {
  EventStatusEnum,
  eventStatusOptions,
  secretKeysOptions,
} from '../../../constants';

export const InfoTab = () => {
  const params = useParams<{ id: string }>();

  const { control, watch } = useFormContext();
  const status = watch('status');

  const queryClient = useQueryClient();

  const eventTypes = queryClient.getQueryData<EventType[]>(['eventTypes']);

  const typesOptions = eventTypes?.map((type) => ({
    id: type.name,
    value: type.id,
  }));

  const refreshCredentialsMutation = useApiMutation<void>(
    `/events/${params.id}/credentials/refresh`,
    {
      method: 'POST',
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['eventDetails', params.id],
        });
        toast.success('Credentials refreshed');
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const copyValue = (value: string) => {
    if (value) {
      navigator.clipboard.writeText(value).then(() => {
        toast('Copied to clipboard');
      });
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <MultiSelect
          name="types"
          label="Type"
          control={control}
          placeholder="Select event types"
          options={typesOptions}
          maxCount={4}
          requiredMark
        />
        <FormField
          control={control}
          name="name"
          render={({ field, formState }) => (
            <FormItem>
              <FormLabel>
                Event name<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Add event name"
                  {...field}
                  error={!!formState.errors['name']}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DatePicker
          control={control}
          name="startDate"
          label="Start date"
          requiredMark
        />

        <Autocomplete
          name="status"
          label="Status"
          control={control}
          placeholder="Select status"
          options={eventStatusOptions}
          className={cn({
            'text-green-500 hover:text-green-500':
              status === EventStatusEnum.Active,
            'text-accent hover:text-accent':
              status === EventStatusEnum.Archived,
          })}
        />
        {secretKeysOptions.map((formField) => (
          <FormField
            control={control}
            name={formField.formKey}
            key={formField.formKey}
            render={({ field }) => (
              <FormItem className={cn(!field.value && 'cursor-not-allowed')}>
                <FormLabel className={cn(!field.value && 'cursor-not-allowed')}>
                  {formField.label}
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      readOnly
                      className={cn(
                        'pr-10 focus-visible:ring-0',
                        !field.value && 'cursor-not-allowed'
                      )}
                      placeholder="generates automatically"
                      {...field}
                    />
                    <div
                      data-testid="copy-button"
                      className={cn(
                        'absolute right-2 top-[7px] cursor-not-allowed',
                        field.value && 'cursor-pointer'
                      )}
                      onClick={() => copyValue(field.value)}
                    >
                      <CopyIcon
                        size={30}
                        color={
                          field.value ? colors.zinc[200] : colors.zinc[800]
                        }
                      />
                    </div>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />
        ))}
        {params.id && (
          <Button
            variant="outline"
            className="max-w-[150px] self-end h-11"
            isLoading={refreshCredentialsMutation.isPending}
            onClick={() => refreshCredentialsMutation.mutate()}
            startIcon={<ArrowClockwiseIcon size={20} />}
          >
            Refresh keys
          </Button>
        )}
      </div>
    </div>
  );
};
